import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Snackbar } from "@mui/material";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../context/UserContext";
import { fetchCampaigns, getCampaignListBillLabel } from "../utils/campaignApi";
import { STATUS_CONFIG } from "../constants/campaignConstants";
import "../styles/component_styles/Campaigns.scss";

const hasAccountType = (accountTypeName) => Boolean(accountTypeName);

function CampaignCard({ campaign, onClick }) {
  const status = STATUS_CONFIG[campaign.status] || STATUS_CONFIG.draft;
  const isVerified = Boolean(campaign.verified_calling_enabled);
  const billLabel = getCampaignListBillLabel(campaign);
  const progressPercent = isVerified && campaign.max_calls > 0
    ? Math.round((campaign.calls_used / campaign.max_calls) * 100)
    : 0;

  return (
    <div className="campaign-card" role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => e.key === "Enter" && onClick()}>
      <div className="campaign-card__top">
        <h3 className="campaign-card__name">{campaign.name || "Untitled Campaign"}</h3>
        <span className="campaign-badge" style={{ backgroundColor: status.bgColor, color: status.color }}>
          {status.label}
        </span>
      </div>

      {billLabel ? (
        <div className="campaign-card__target">{billLabel}</div>
      ) : null}

      <div className="campaign-card__stats">
        {isVerified ? (
          <>
            <div className="campaign-card__stat">
              <p className="campaign-card__stat-label">Calls Made</p>
              <p className="campaign-card__stat-value">{campaign.calls_used}</p>
            </div>
            <div className="campaign-card__stat">
              <p className="campaign-card__stat-label">Remaining</p>
              <p className="campaign-card__stat-value">{campaign.remaining_calls ?? "—"}</p>
            </div>
            <div className="campaign-card__stat">
              <p className="campaign-card__stat-label">Budget</p>
              <p className="campaign-card__stat-value">${Number(campaign.total_cost || 0).toFixed(2)}</p>
            </div>
          </>
        ) : (
          <>
            <div className="campaign-card__stat">
              <p className="campaign-card__stat-label">Calls</p>
              <p className="campaign-card__stat-value">{campaign.call_completions ?? campaign.action_completions ?? 0}</p>
            </div>
            <div className="campaign-card__stat">
              <p className="campaign-card__stat-label">Emails</p>
              <p className="campaign-card__stat-value">{campaign.email_completions || 0}</p>
            </div>
            <div className="campaign-card__stat">
              <p className="campaign-card__stat-label">Type</p>
              <p className="campaign-card__stat-value">Free</p>
            </div>
          </>
        )}
      </div>

      {isVerified ? (
        <div className="campaign-progress">
          <div className="campaign-progress__bar">
            <div
              className="campaign-progress__fill"
              style={{
                width: `${Math.min(100, Math.max(0, progressPercent))}%`,
                backgroundColor: progressPercent >= 90 ? "#dc2626" : "#047857",
              }}
            />
          </div>
          <div className="campaign-progress__text">{progressPercent}% used</div>
        </div>
      ) : null}
    </div>
  );
}

export default function CampaignListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { accountTypeName, loadingUser } = useUser();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });
  const [campaigns, setCampaigns] = useState([]);
  const [query, setQuery] = useState("");
  const [showHowModal, setShowHowModal] = useState(false);

  const eligible = hasAccountType(accountTypeName);

  useEffect(() => {
    const flash = location.state?.flash;
    if (!flash) return;
    const severity = location.state?.flashSeverity === "error" ? "error" : "success";
    setSnack({ open: true, severity, message: flash });
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  const loadCampaigns = useCallback(async () => {
    if (!eligible) {
      setLoading(false);
      setCampaigns([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await fetchCampaigns();
      setCampaigns(data);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load campaigns.");
    } finally {
      setLoading(false);
    }
  }, [eligible]);

  useEffect(() => {
    if (!loadingUser) loadCampaigns();
  }, [loadingUser, loadCampaigns]);

  const filteredCampaigns = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return campaigns;
    return campaigns.filter((campaign) => {
      const statusLabel = (STATUS_CONFIG[campaign.status] || STATUS_CONFIG.draft).label.toLowerCase();
      const billLabel = getCampaignListBillLabel(campaign).toLowerCase();
      return (
        campaign.name.toLowerCase().includes(q) ||
        billLabel.includes(q) ||
        statusLabel.includes(q)
      );
    });
  }, [campaigns, query]);

  if (loadingUser || loading) {
    return (
      <div className="campaigns-page">
        <div className="campaign-loading">
          <CircularProgress size={24} />
          <p style={{ marginTop: 10 }}>Loading campaigns...</p>
        </div>
      </div>
    );
  }

  if (!eligible) {
    return (
      <div className="campaigns-page">
        <div className="campaign-locked">
          <h3>Complete account setup</h3>
          <p>Finish setting up your account to create campaigns.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="campaigns-page">
      <div className="campaigns-page__header">
        <h1 className="campaigns-page__title">Campaigns</h1>
        <div className="campaigns-page__header-actions">
          <button
            type="button"
            className="campaign-btn campaign-btn--primary campaigns-page__create-btn"
            onClick={() => navigate("/campaigns/create")}
          >
            Create Campaign
          </button>
        </div>
      </div>

      {error ? <div className="campaign-error"><h3>Error</h3><p>{error}</p></div> : null}

      <input
        className="campaigns-page__search"
        type="search"
        placeholder="Search campaigns by name, bill, or status..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label="Search campaigns"
      />

      {filteredCampaigns.length === 0 ? (
        campaigns.length > 0 ? (
          <div className="campaign-empty">
            <h3>No matching campaigns</h3>
            <p>Try a different search term, or clear the search to see all campaigns.</p>
            <Button
              variant="text"
              onClick={() => setQuery("")}
              sx={{ textTransform: "none", color: "#047857", fontWeight: 700 }}
            >
              Clear search
            </Button>
          </div>
        ) : (
        <div className="campaign-empty campaign-empty--featured">
          <div className="campaign-empty__head">
            <div className="campaign-empty__icon" aria-hidden="true">
              <CampaignOutlinedIcon />
            </div>
            <h3>Start your first campaign</h3>
          </div>
          <p>
            Share a free campaign page with a script and contacts, then add verified calling when you
            need tracked outcomes.
          </p>
          <ul className="campaign-empty__list">
            <li>Share a public page that works without login</li>
            <li>Use a custom contact list or each viewer&apos;s own reps</li>
            <li>Optionally enable paid verified calling for measured outcomes</li>
          </ul>
          <div className="campaign-empty__actions">
            <button
              type="button"
              className="campaign-btn campaign-btn--primary"
              onClick={() => navigate("/campaigns/create")}
            >
              Create Campaign
            </button>
            <button
              type="button"
              className="campaign-btn campaign-empty__secondary-btn"
              onClick={() => setShowHowModal(true)}
            >
              How verified calls work
            </button>
          </div>
        </div>
        )
      ) : (
        <div className="campaigns-list">
          {filteredCampaigns.map((campaign) => (
            <CampaignCard
              key={campaign.id}
              campaign={campaign}
              onClick={() => navigate(`/campaigns/${campaign.id}`)}
            />
          ))}
        </div>
      )}

      <Dialog open={showHowModal} onClose={() => setShowHowModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: "#0f172a" }}>How Verified Calls Work</DialogTitle>
        <DialogContent dividers>
          <p style={{ marginTop: 0, color: "#0f172a" }}>
            Verified call campaigns connect users calling about your selected bill to the correct office while tracking campaign outcomes.
          </p>
          <ol style={{ margin: 0, paddingLeft: 18, color: "#0f172a" }}>
            <li>Create your campaign and choose a target bill.</li>
            <li>Set a schedule and budget cap for verified calling.</li>
            <li>Complete payment to activate verified calling.</li>
            <li>Monitor call outcomes in your campaign detail page.</li>
            <li>Only identity-verified callers count toward tracked verified-calling totals. Anyone can still call from your take-action page.</li>
          </ol>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
